/**
 * 将单个 block 转换为图片
 * 使用 puppeteer 将 block 渲染为 HTML 后截图
 */

import mjml2html from "mjml";
import puppeteer, { type Browser } from "puppeteer";
import { EditorCore } from "../../src/utils/EditorCore";
import type { Element, PageElement } from "../../src/typings";
import { BlockManager } from "../../src/utils/BlockManager";
import { BasicType } from "../../src/constants";
import { uploadImage } from "../utils/upload";
import { randomUUID } from "crypto";

export interface ConvertBlockToImageInput {
  /** 单个 block 元素 */
  block: Element;
  /** 图片宽度（像素），默认自动 */
  width?: number;
  /** 图片高度（像素），默认自动 */
  height?: number;
  /** 图片格式，默认 "png" */
  format?: "png" | "jpeg";
  /** 渲染模式 */
  mode?: "testing" | "production";
  /** 响应式模式 */
  displayMode?: "desktop-mobile" | "only-desktop" | "only-mobile";
  /** 合并标签数据 */
  mergetagsData?: Record<string, any>;
}

export interface ConvertBlockToImageOutput {
  success: boolean;
  /** CDN 图片 URL（如果上传成功） */
  imageUrl?: string;
  /** 图片宽度 */
  width?: number;
  /** 图片高度 */
  height?: number;
  /** 错误信息 */
  error?: string;
}

/**
 * 将单个 block 转换为图片
 */
export async function convertBlockToImage(
  input: ConvertBlockToImageInput
): Promise<ConvertBlockToImageOutput> {
  let browser: Browser | null = null;

  try {
    const {
      block,
      width,
      height,
      format = "png",
      mode = "production",
      displayMode = "desktop-mobile",
      mergetagsData,
    } = input;

    if (!block || !block.type) {
      throw new Error("Invalid block: missing type");
    }

    // 验证 block 类型
    const blockDef = BlockManager.getBlockByType(block.type);
    if (!blockDef) {
      throw new Error(`Block type "${block.type}" not found`);
    }

    // 授权
    await EditorCore.auth("ryanmao");

    // 确定要转换的元素
    // 如果 block 不是 page，需要包装在 page 中以确保生成完整的 MJML 结构

    const pageBlockDef = BlockManager.getBlockByType(BasicType.PAGE);
    if (!pageBlockDef?.create) {
      throw new Error(`Block type not found: ${BasicType.PAGE}`);
    }
    const SECTION_CSS_CLASS = "SECTION_BLOCK";
    const pageElement = pageBlockDef.create({
      attributes: {
        "background-color": "#ffffff",
        "margin-top": "0px",
        "margin-bottom": "0px",
      },
      data: {
        breakpoint: "480px",
      },
      children: [
        {
          ...block,
          attributes: {
            ...block.attributes,
            "css-class": SECTION_CSS_CLASS,
          },
        },
      ],
    });

    // 转换为 MJML（element 必须是 page 类型才能生成完整的 MJML 结构）
    const mjml = EditorCore.toMJML({
      element: pageElement,
      pageElement: pageElement as PageElement,
      mode,
      beautify: false,
      displayMode,
      mergetagsData,
    });

    // 转换为 HTML
    const mjmlResult = mjml2html(mjml, {
      validationLevel: "skip",
      minify: false,
    });

    if (mjmlResult.errors && mjmlResult.errors.length > 0) {
      console.warn("MJML validation errors:", mjmlResult.errors);
    }

    const html = mjmlResult.html;

    // 使用 puppeteer 截图
    browser = await puppeteer.launch({
      headless: true,
      args: ["--no-sandbox", "--disable-setuid-sandbox"],
    });
    const page = await browser.newPage();

    await page.setContent(html, {
      waitUntil: "networkidle0",
    });

    // 设置视口
    await page.setViewport({
      width: width || 600,
      height: height || 1000,
    });

    // 生成文件名和 MIME 类型
    const mimeType = format === "jpeg" ? "image/jpeg" : "image/png";
    const fileExtension = format === "jpeg" ? "jpg" : "png";
    const fileName = `block_${randomUUID().replace(/-/g, "")}.${fileExtension}`;

    // 等待页面加载完成
    await page.waitForSelector("body", { timeout: 5000 });

    // MJML 已转换为 HTML，查找 HTML 元素（不是 MJML 元素）
    // 尝试多种选择器方式
    let targetElement = await page.$(`.${SECTION_CSS_CLASS}`);

    // 如果找不到，尝试查找包含该 class 的任何元素
    if (!targetElement) {
      targetElement = await page.$(`[class*="${SECTION_CSS_CLASS}"]`);
    }

    // 如果还是找不到，查找第一个有内容的 table（MJML 通常转换为 table）
    if (!targetElement) {
      targetElement = await page.$("table[width]");
    }

    // 如果还是找不到，查找 body 内的第一个直接子元素（通常是主要内容容器）
    if (!targetElement) {
      const selector = await page.evaluate(() => {
        const body = document.body;
        // 查找第一个有宽度的 table 或 div
        const firstTable = body.querySelector("table[width]");
        if (firstTable) {
          // 返回一个唯一的选择器
          const index = Array.from(
            body.querySelectorAll("table[width]")
          ).indexOf(firstTable);
          return index === 0
            ? "table[width]"
            : `table[width]:nth-of-type(${index + 1})`;
        }

        // 或者查找第一个有内容的 div
        const firstDiv = body.querySelector("div[style*='width']");
        if (firstDiv) {
          const index = Array.from(
            body.querySelectorAll("div[style*='width']")
          ).indexOf(firstDiv);
          return index === 0
            ? "div[style*='width']"
            : `div[style*='width']:nth-of-type(${index + 1})`;
        }

        // 最后返回 body 的第一个子元素的选择器
        if (body.firstElementChild) {
          const tagName = body.firstElementChild.tagName.toLowerCase();
          return `${tagName}:first-child`;
        }

        return null;
      });

      if (selector) {
        targetElement = await page.$(selector);
      }
    }

    if (!targetElement) {
      // 调试：输出 HTML 结构
      const bodyHTML = await page.evaluate(() => {
        return document.body.innerHTML.substring(0, 2000);
      });
      console.error("HTML content:", bodyHTML);
      throw new Error(
        `Target element not found. Block type: ${block.type}, Looking for class: ${SECTION_CSS_CLASS}`
      );
    }

    const screenshot = await targetElement.screenshot({
      type: format,
      encoding: "binary",
    });

    await browser.close();
    browser = null;

    // 将截图转换为 Buffer（puppeteer 可能返回 Buffer、Uint8Array 或 string）
    let imageBuffer: Buffer;
    if (screenshot instanceof Buffer) {
      imageBuffer = screenshot;
    } else if (screenshot instanceof Uint8Array) {
      imageBuffer = Buffer.from(screenshot);
    } else {
      imageBuffer = Buffer.from(screenshot as string, "binary");
    }

    // 直接上传到 CDN
    let imageUrl: string | undefined;
    try {
      imageUrl = await uploadImage(imageBuffer, fileName, mimeType);
    } catch (uploadError) {
      console.warn("Failed to upload image to CDN:", uploadError);
      throw new Error(
        `Failed to upload image to CDN: ${
          uploadError instanceof Error
            ? uploadError.message
            : String(uploadError)
        }`
      );
    }

    return {
      success: true,
      imageUrl,
    };
  } catch (error) {
    if (browser) {
      await browser.close().catch(() => {
        // 忽略关闭错误
      });
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export const CONVERT_BLOCK_TO_IMAGE_TOOL = {
  name: "convert_block_to_image",
  description: `将单个 block 元素转换为图片（PNG 或 JPEG）。
支持任何类型的 block，包括 page、section、column 或单个元素。

输入参数:
- block: 单个 block 元素（必填，必须包含 type 字段）
- pageElement: 可选的页面上下文（如果 block 不是 page 类型，会自动创建最小 page）
- width: 图片宽度（像素，可选，默认自动）
- height: 图片高度（像素，可选，默认自动）
- format: 图片格式，"png" 或 "jpeg"（默认 "png"）
- mode: 渲染模式，"testing" 或 "production"（默认 "production"）
- displayMode: 响应式模式，"desktop-mobile" | "only-desktop" | "only-mobile"（默认 "desktop-mobile"）
- mergetagsData: 模板变量数据（可选，用于渲染模板变量）

输出:
- success: boolean - 是否成功
- imageBase64: Base64 编码的图片数据（如果成功）
- imageUrl: CDN 图片 URL（如果上传成功）
- width: 图片宽度（像素）
- height: 图片高度（像素）
- error: 错误信息（如果失败）

示例:
\`\`\`json
{
  "block": {
    "type": "standard-section",
    "data": {},
    "attributes": {
      "background-color": "#f0f0f0"
    },
    "children": [...]
  },
  "format": "png",
  "mode": "production"
}
\`\`\``,
  inputSchema: {
    type: "object" as const,
    properties: {
      block: {
        type: "object",
        description: "单个 block 元素，必须包含 type 字段",
      },
      pageElement: {
        type: "object",
        description: "可选的页面上下文（如果 block 不是 page 类型）",
      },
      width: {
        type: "number",
        description: "图片宽度（像素），默认自动",
      },
      height: {
        type: "number",
        description: "图片高度（像素），默认自动",
      },
      format: {
        type: "string",
        enum: ["png", "jpeg"],
        description: "图片格式",
      },
      mode: {
        type: "string",
        enum: ["testing", "production"],
        description: "渲染模式",
      },
      displayMode: {
        type: "string",
        enum: ["desktop-mobile", "only-desktop", "only-mobile"],
        description: "响应式模式",
      },
      mergetagsData: {
        type: "object",
        description: "模板变量数据，用于渲染模板变量",
      },
    },
    required: ["block"],
  },
};
